/**
 * useGatheringAutoSave — Auto-save questionnaire data to backend every 30s.
 *
 * Debounced: only fires if data changed since last save.
 * Uses PATCH /crm/calls/{id}/gathering (idempotent merge).
 */

import { useEffect, useRef, useCallback } from 'react';
import { saveGatheringData } from '@/services/callOrchestrator';
import type { QuestionnaireData } from '@/types/callSidebar';

const AUTO_SAVE_INTERVAL_MS = 30_000;

export function useGatheringAutoSave(
  callId: string | null,
  questionnaire: QuestionnaireData,
  enabled: boolean,
) {
  const lastSavedRef = useRef<string>('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const doSave = useCallback(async () => {
    if (!callId || !enabled) return;

    const serialized = JSON.stringify(questionnaire);
    if (serialized === lastSavedRef.current) return; // No changes

    try {
      await saveGatheringData(callId, questionnaire as unknown as Record<string, unknown>);
      lastSavedRef.current = serialized;
    } catch {
      // Silently fail — data is still in sessionStorage
    }
  }, [callId, questionnaire, enabled]);

  useEffect(() => {
    if (!enabled || !callId) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(doSave, AUTO_SAVE_INTERVAL_MS);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, callId, doSave]);

  // Save on unmount if there are unsaved changes
  useEffect(() => {
    return () => {
      if (callId && enabled) {
        const serialized = JSON.stringify(questionnaire);
        if (serialized !== lastSavedRef.current) {
          // Fire-and-forget save
          saveGatheringData(callId, questionnaire as unknown as Record<string, unknown>).catch(() => {});
        }
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
